/* eslint-disable no-restricted-syntax */
import { t } from "i18next";
import lod_ from "lodash";
import { Divider, Icon, InputAdornment } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDInput from "components/Basics/MDInput";
import ButtonMenu from "components/Custom/ButtonMenu";
import DateFilter from "components/Custom/Filters/DateFilter";
import { getLocalStorageFrontValues } from "components/Custom/Filters/filters";
import { getLabel } from "helpers/dictionary";
import { useDispatch, useSelector } from "react-redux";
import { deleteFilter, setFilter } from "redux-react/reducers/filtersReducers";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import InputString from "./filters/InputString";

/**
 * Parse filters defined by string into
 * an component's array
 * @param {[String]} filters
 */
function useFilters(page, filters) {
	const storeFilters = useSelector(state => state.filters);
	const profile = useSelector(selectCurrentProfile);
	const language = profile?.language || "fr";
	const assistantID = profile?.assistantID;
	const dispatch = useDispatch();

	let newComponentFilters = [];

	function setFilterToStore(assistantID, page, filter, front, back) {
		dispatch(
			setFilter({
				assistantID,
				page,
				attribute: filter.attribute,
				front,
				back: {
					name: filter.attribute,
					value: back,
					type: filter.type,
					dictionaryPath: filter.dictionaryPath,
					links: filter.links
				}
			})
		);
	}

	const updateStoreFilter = (value, filter, page) => {
		if (!value.trim()) {
			dispatch(
				deleteFilter({
					assistantID,
					page,
					attribute: filter.attribute
				})
			);
		} else {
			const mongoRegex = { $regex: value, $options: "i" };
			setFilterToStore(assistantID, page, filter, value, mongoRegex);
		}
	};

	const updateStoreFilterDebounce = lod_.debounce((value, filter, page) => {
		if (!value.trim()) {
			dispatch(
				deleteFilter({
					assistantID,
					page,
					attribute: filter.attribute
				})
			);
		} else {
			const mongoRegex = { $regex: value, $options: "i" };
			setFilterToStore(assistantID, page, filter, value, mongoRegex);
		}
	}, 300);

	for (let filter of filters) {
		switch (filter.type) {
			case "boolean":
				newComponentFilters.push(
					<MDBox key={filter.attribute} mr={1}>
						<ButtonMenu
							fullHeight
							title={getLabel(filter.label, language)}
							type="boolean"
							inverted={filter.inverted}
							optionnal={filter.optionnal}
							storage={getLocalStorageFrontValues(
								assistantID,
								page,
								filter.attribute,
								storeFilters
							)}
							handleUpdate={items => {
								if (lod_.isEmpty(items)) {
									let filterObject = {
										assistantID,
										page,
										attribute: filter.attribute
									};

									dispatch(deleteFilter(filterObject));
								} else {
									let value = lod_.isObject(items.value) ? items.value : { $in: [items.value] };
									setFilterToStore(assistantID, page, filter, items, value);
								}
							}}
						/>
					</MDBox>
				);
				break;
			case "datetime":
			case "date":
				newComponentFilters.push(
					<MDBox mr={1} key={filter.attribute} display="flex">
						<DateFilter
							assistantID={assistantID}
							page={page}
							storage={getLocalStorageFrontValues(
								assistantID,
								page,
								filter.attribute,
								storeFilters
							)}
							tooltip
							attribute={filter.attribute}
							title={
								getLabel(filter.label, language) || filter.label || t(`FILTERS.${filter.attribute}`)
							}
						/>
					</MDBox>
				);
				break;
			case "code":
				newComponentFilters.push(
					<MDBox key={filter.dictionaryPath} mr={1}>
						<ButtonMenu
							fullHeight
							title={getLabel(filter.label, language)}
							items={filter.values}
							storage={getLocalStorageFrontValues(
								assistantID,
								page,
								filter.attribute,
								storeFilters
							)}
							handleUpdate={items => {
								if (!items.length) {
									let filterObject = {
										assistantID,
										page,
										attribute: filter.attribute
									};

									dispatch(deleteFilter(filterObject));
								} else {
									let values = items.map(i => i.value);
									setFilterToStore(assistantID, page, filter, items, { $in: values });
								}
							}}
						/>
					</MDBox>
				);
				break;
			default:
				if (filter.type === "string") {
					newComponentFilters.push(
						<InputString
							filter={filter}
							page={page}
							language={language}
							updateStoreFilterDebounce={updateStoreFilterDebounce}
							updateStoreFilter={updateStoreFilter}
						/>
					);
				} else {
					newComponentFilters.push(
						<MDBox key={filter.attribute} mr={1}>
							<MDInput
								label={filter.label.fr}
								type={filter.type}
								value={getLocalStorageFrontValues(
									assistantID,
									page,
									filter.attribute,
									storeFilters
								)}
								storage={getLocalStorageFrontValues(
									assistantID,
									page,
									filter.attribute,
									storeFilters
								)}
								onChange={e => {
									if (lod_.isNil(e.target.value) || lod_.isEmpty(e.target.value)) {
										dispatch(
											deleteFilter({
												assistantID,
												page,
												attribute: filter.attribute
											})
										);
									} else {
										setFilterToStore(assistantID, page, filter, e.target.value, e.target.value);
									}
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start" sx={{ ml: 1, mr: 0 }}>
											<Icon size="small">filter_alt</Icon>
										</InputAdornment>
									)
								}}
								placeholder={`Filtrer ${getLabel(filter.label, language)} ...`}
							/>
						</MDBox>
					);
				}
				break;
		}
	}

	if (!lod_.isEmpty(newComponentFilters)) {
		newComponentFilters.unshift(
			<Divider
				variant="vertical"
				sx={{
					marginLeft: 1,
					width: "2px",
					minHeight: "5vh",
					height: "100%",
					color: "#ccc",
					opacity: "0.6",
					backgroundImage:
						"linear-gradient(to right, rgba(52, 71, 103, 0), rgba(52, 71, 103, 0.4), rgba(52, 71, 103, 0)) !important"
				}}
			/>
		);
	}

	return newComponentFilters;
}

export default useFilters;
